// src/components/IPDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress
} from '@mui/material';
import { Close as CloseIcon, Block as BlockIcon, MoreVert as MoreVertIcon, Check as CheckIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const IPDetailsModal = ({ open, onClose, detailViewData = [], onWhitelist, onBlockIP }) => {
    const theme = useTheme();
    const {
        divider,
        mode
    } = theme.palette;

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentItemId, setCurrentItemId] = useState(null);
    const [currentItem, setCurrentItem] = useState(null);
    const [loading, setLoading] = useState(false);



    const handleMenuOpen = (event, item) => {
        setAnchorEl(event.currentTarget);
        setCurrentItemId(item.id);
        setCurrentItem(item);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleWhitelist = async () => {
        if (!currentItemId) return;

        setLoading(true);
        try {
            if (onWhitelist) {
                await onWhitelist(currentItemId, currentItem);
            } else {
                console.log(`Whitelist IP with ID: ${currentItemId}`);
            }
        } catch (error) {
            console.error('Error whitelisting IP:', error);
        } finally {
            setLoading(false);
            handleMenuClose();
        }
    };

    const handleBlockIP = async () => {
    if (!currentItemId) return;

    setLoading(true);
    try {
      if (onBlockIP) {
        await onBlockIP(currentItemId, currentItem);
      } else {
        console.log(`Block IP with ID: ${currentItemId}`);
      }
    } catch (error) {
      console.error('Error blocking IP:', error);
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  // Count unique users and activities from the detailViewData
  const countUniqueUsers = () => {
    if (!detailViewData.length) return 0;
    const uniqueUsers = new Set();
    detailViewData.forEach(item => {
      if (item.user) {
        uniqueUsers.add(item.user);
      }
    });
    return uniqueUsers.size;
  };

  const countActivities = () => {
    if (!detailViewData.length) return { logins: 0, registrations: 0 };
    const activities = { logins: 0, registrations: 0 };
    detailViewData.forEach(item => {
      if (item.type === 'registration') {
        activities.registrations++;
      } else {
        activities.logins++;
      }
    });
    return activities;
  };

  const uniqueUserCount = countUniqueUsers();
  const activities = countActivities();

    // Reset states when closing the modal
    useEffect(() => {
        if (!open) {
            setAnchorEl(null);
            setCurrentItemId(null);
            setCurrentItem(null);
            setLoading(false);
        }
    }, [open]);

    const getInitials = (name) => {
        if (!name) return '';
        return name.split(' ').map(part => part[0] || '').join('');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            props={{
                sx: {
                    width: { xs: '95%', sm: '90%', md: '80%' },
                    maxHeight: '90vh',
                    bgcolor: mode === 'dark' ? '#292929' : '#ffffff',
                    color: mode === 'dark' ? '#fff' : '#000',
                    borderRadius: '8px'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${divider}`,
                padding: '16px 24px',
                color: mode === 'dark' ? '#fff' : '#000'
            }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>View Details</Typography>
                  {detailViewData.length > 0 && (
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)', mt: 0.5 }}>
                    {uniqueUserCount} user{uniqueUserCount !== 1 ? 's' : ''} • {activities.logins} login{activities.logins !== 1 ? 's' : ''} • {activities.registrations} registration{activities.registrations !== 1 ? 's' : ''}
                  </Typography>
                  )}
                </Box>
                <IconButton onClick={onClose} sx={{ color: mode === 'dark' ? '#fff' : '#000' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ mt: 2, p: 0, overflowX: 'auto' }}>
                {detailViewData.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <Typography variant="body1" sx={{ color: mode === 'dark' ? '#fff' : '#000' }}>
                            No data available
                        </Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0', fontWeight: 500 }}>IP Address</TableCell>
                                <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0', fontWeight: 500 }}>Location</TableCell>
                                <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0', fontWeight: 500 }}>Associated User(s)</TableCell>
                                <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0', fontWeight: 500 }}>Access Timestamp(s)</TableCell>
                                <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0', fontWeight: 500 }}>Activity Type</TableCell>
                                {/* <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0', fontWeight: 500 }}>Action</TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {detailViewData.map((item) => {


                                return (
                                    <TableRow
                                        key={item.id}
                                        hover
                                        sx={{
                                            '&:hover': { bgcolor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' },
                                            borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0'
                                        }}
                                    >
                                        <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0' }}>
                                            {item.ipAddress || 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {/* item.flag contains country code from backend */}
                                                {item.flag && (
                                                    <img
                                                        src={`https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/${item.flag.toLowerCase()}.svg`}
                                                        alt={`${item.location} flag`}
                                                        style={{ width: 24, height: 16, objectFit: 'cover' }}
                                                        onError={(e) => { e.target.style.display = 'none' }}
                                                    />
                                                )}
                                                {item.location || 'Unknown'}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {item.user && (
                                                    <Box
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: '50%',
                                                            bgcolor: mode === 'dark' ? '#333' : '#e0e0e0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: mode === 'dark' ? '#fff' : '#000',
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        {getInitials(item.user)}
                                                    </Box>
                                                )}
                                                {item.user || 'Unknown'}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0' }}>
                                            {item.accessTimestamp || 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ color: mode === 'dark' ? '#fff' : '#000', borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0' }}>
                                            <Chip 
                                                label={item.type === 'registration' ? 'Registration' : 'Login'}
                                                size="small"
                                                sx={{ 
                                                    bgcolor: item.type === 'registration' ? 
                                                        (mode === 'dark' ? '#4a5568' : '#e2e8f0') : 
                                                        (mode === 'dark' ? '#2d5a27' : '#c8e6c9'),
                                                    color: item.type === 'registration' ? 
                                                        (mode === 'dark' ? '#cbd5e0' : '#4a5568') : 
                                                        (mode === 'dark' ? '#a5d6a7' : '#2e7d32'),
                                                    fontWeight: 500
                                                }}
                                            />
                                        </TableCell>
                                        {/* <TableCell sx={{ borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0' }}>
                                            <IconButton
                                                sx={{ color: mode === 'dark' ? '#fff' : '#000' }}
                                                onClick={(e) => handleMenuOpen(e, item)}
                                                disabled={loading}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell> */}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}

                {/* <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    props={{
                        sx: {
                            bgcolor: mode === 'dark' ? '#111111' : '#ffffff',
                            boxShadow: mode === 'dark' ? '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)' : '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                            minWidth: { xs: '200px', sm: '220px' }
                        }
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                    }}
                >
                    <MenuItem
                        onClick={handleWhitelist}
                        disabled={loading}
                        sx={{
                            color: mode === 'dark' ? '#fff' : '#000',
                            '&:hover': { bgcolor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' },
                            py: 1.5
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {loading ? <CircularProgress size={20} /> : <CheckIcon fontSize="small" sx={{ color: 'green' }} />}
                            Whitelist
                        </Box>
                    </MenuItem>
                    <MenuItem
                        onClick={handleBlockIP}
                        disabled={loading}
                        sx={{
                            color: '#ff5252',
                            '&:hover': { bgcolor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' },
                            py: 1.5
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {loading ? <CircularProgress size={20} color="error" /> : <BlockIcon fontSize="small" />}
                            Block IP
                        </Box>
                    </MenuItem>
                </Menu> */}
            </DialogContent>
        </Dialog>
    );
};

export default IPDetailsModal;